import React, { useEffect, useMemo, useState } from "react";
import { Modal, Tabs } from "@hid-galaxy-ui/galaxy-react";
import sanitizeHtml from "sanitize-html";
import "./GuidanceModal.scss";

interface GuidanceModalProps {
  onClose: () => void;
  technology: string;
  integration: string;
}

interface GuidanceTab {
  tab_name: string;
  content: string;
}

interface GuidanceItem {
  technology: string[];
  integration: string[];
  tabs: GuidanceTab[];
}

const TAB_ORDER = [
  "Validation Process Overview",
  "Validation Pre-requisites",
  "Post Validation Guidance",
] as const;

const normalizeValue = (value: string): string => value.trim().toLowerCase();

const toKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeGuidanceHtml = (html: string): string =>
  (html || "")
    .replace(/<p>\s*(?:<br\s*\/?>|&nbsp;|\u00A0|\s)*<\/p>/gi, "")
    .replace(/\u00A0/g, " ");

const GuidanceModal: React.FC<GuidanceModalProps> = ({
  onClose,
  technology,
  integration,
}) => {
  const [guideLinesData, setGuideLinesData] = useState<GuidanceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const fetchGuideLines = async () => {
      try {
        setIsLoading(true);
        setHasLoadError(false);
        const response = await fetch("/docs/guidanceProcessAndSteps.json");
        if (!response.ok) {
          throw new Error("Failed to load guidance content");
        }
        const data = (await response.json()) as GuidanceItem[];
        if (!isCancelled) {
          setGuideLinesData(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!isCancelled) {
          setGuideLinesData([]);
          setHasLoadError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchGuideLines();

    return () => {
      isCancelled = true;
    };
  }, []);

  const matchedGuidance = useMemo(() => {
    const normalizedTechnology = normalizeValue(technology || "");
    const normalizedIntegration = normalizeValue(integration || "");

    if (!normalizedTechnology || !normalizedIntegration) {
      return undefined;
    }

    return guideLinesData.find((item) => {
      const hasTechnologyMatch = item.technology.some(
        (tech) => normalizeValue(tech) === normalizedTechnology
      );
      const hasIntegrationMatch = item.integration.some(
        (integrationItem) =>
          normalizeValue(integrationItem) === normalizedIntegration
      );
      return hasTechnologyMatch && hasIntegrationMatch;
    });
  }, [guideLinesData, integration, technology]);

  const tabItems = useMemo(() => {
    if (!matchedGuidance) {
      return [];
    }

    const tabOrderMap = new Map<string, number>(
      TAB_ORDER.map((label, index) => [label, index])
    );

    return matchedGuidance.tabs
      .filter((tab) => tabOrderMap.has(tab.tab_name))
      .filter((tab) => !!tab.content?.trim())
      .sort(
        (left, right) =>
          (tabOrderMap.get(left.tab_name) ?? Number.MAX_SAFE_INTEGER) -
          (tabOrderMap.get(right.tab_name) ?? Number.MAX_SAFE_INTEGER)
      )
      .map((tab) => ({
        key: toKey(tab.tab_name),
        label: tab.tab_name,
        content: (
          <div
            className="guidance-tab-content"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(normalizeGuidanceHtml(tab.content || ""), {
                allowedTags: [
                  "p",
                  "br",
                  "strong",
                  "b",
                  "em",
                  "i",
                  "u",
                  "h1",
                  "h2",
                  "h3",
                  "h4",
                  "h5",
                  "h6",
                  "ul",
                  "ol",
                  "li",
                  "a",
                ],
                allowedAttributes: {
                  a: ["href", "target", "rel"],
                },
                allowedSchemes: ["http", "https", "mailto"],
                transformTags: {
                  a: sanitizeHtml.simpleTransform("a", {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }),
                },
              }),
            }}
          />
        ),
      }));
  }, [matchedGuidance]);

  return (
    <Modal
      headerText="Guidance and process steps"
      width={900}
      onClose={onClose}
      className="guidance-modal"
    >
      <div className="guidance-modal-container">
        {isLoading && (
          <div className="guidance-empty-state">
            Guidance content is loading...
          </div>
        )}
        {!isLoading && (hasLoadError || tabItems.length === 0) && (
          <div className="guidance-empty-state">
            No guidance content is available for this technology and integration.
          </div>
        )}
        {!isLoading && !hasLoadError && tabItems.length > 0 && (
          <Tabs theme="frameless" items={tabItems} onClick={() => {}} />
        )}
      </div>
    </Modal>
  );
};

export default GuidanceModal;

